import { defineStore } from 'pinia'
import { computed, ref } from 'vue'
import { useRoute } from 'vue-router'
import { useFetch } from '@/modules/shared/composables/use-fetch'
import { CID } from '@/modules/shared/utils/store.types'
import { Money } from '@/modules/shared/utils/money'
import { useNotificationStore } from '@/modules/shared/stores/notification-store'
import { clearItems, addItems } from '@/modules/shared/utils/store'

///////////////////////////////////////////////////////////////////////////////
// Types
///////////////////////////////////////////////////////////////////////////////

type ID = number | string

export type Investment = {
  id: ID
  accrued_interest: Money
  current_price: Money
  current_shares: number
  current_value: Money
  initial_price: Money
  initial_shares: number
  initial_value: Money
  disbursement: Money
  expiration_date: string

  method_label: string
  minimum_annual_return: Money
  investment_type: string
  investor_cid: string
  total_shares: number
  valuation_cap: Money

  company: {
    id: ID
    name: string
    logo_url: string
    can_mark_as_loss: boolean
    can_undo_loss_distribution: boolean
  }
  industry: {
    id: ID
    name: string
  }
  investor: {
    _cid: CID
    id: ID
    name: string
  }
  term_sheet: {
    id: ID
    name: string
    terms: any[]
  }
}

type LossInvestment = {
  _cid: CID
  id: string
  can_be_mark_as_loss: boolean
  company: {
    id: string
    name: string
  }
  initial_value: Money
  investment_type: string
  is_loss: boolean
  loss_value: Money
  name: string
  term_sheet: {
    id: string
    name: string
  }
}

///////////////////////////////////////////////////////////////////////////////
// Store
///////////////////////////////////////////////////////////////////////////////

type LossInvestmentMap = Map<CID, LossInvestment>

export const usePortfolioStore = defineStore('investing/portfolioStore', () => {
  const notificationStore = useNotificationStore()
  const route = useRoute()
  const baseUrl = computed(() => `/${route.params.slug}/investing`)

  const items = ref([])
  const investments = computed(() => items.value)

  const loss_investment_items = ref<LossInvestmentMap>(new Map())
  const loss_investments = computed(() =>
    Array.from(loss_investment_items.value.keys()).map((key) => loss_investment_items.value.get(key)),
  )

  const fetchInvestments = async (cid: string, params: any) => {
    reset()
    const [resource, id] = cid.split(':')
    const { data, error } = await useFetch(`${baseUrl.value}/${resource}/${id}/investments?as_of=${params.as_of}`)
      .get()
      .json<{ data: Investment[] }>()

    if (error.value) {
      console.error(error.value)
      notificationStore.enqueue('error', 'Investments failed to load')
      return
    }

    items.value = data.value.data
  }

  const fetchPortfolio = async (payload: { _cid: string; as_of_date: Date }) => {
    reset()
    const { data, error } = await useFetch(
      `${baseUrl.value}/portfolio?cid=${payload._cid}&as_of_date=${payload.as_of_date}`,
    )
      .get()
      .json<{ data: Investment[] }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      notificationStore.enqueue('error', 'Investments failed to load')
      return
    }

    items.value = data.value.data
  }

  const fetchLossInvestments = async (investable_type, investable_id) => {
    const { data, error } = await useFetch(`${baseUrl.value}/${investable_type}/${investable_id}/loss_investments`)
      .get()
      .json<{ data: LossInvestment[] }>()

    if (error.value) {
      // TODO: handle error (e.g., display a message to the user)
      console.error(error.value)
      return
    }

    clearItems(loss_investment_items)
    addItems(loss_investment_items, data.value.data)
  }

  const reset = () => {
    items.value = []
  }

  return {
    items,
    investments,
    loss_investments,

    reset,
    fetchInvestments,
    fetchLossInvestments,
    fetchPortfolio,
  }
})
